import React, { useState, useEffect, useRef } from 'react';
import './client.css';
import useTheme from '../ColorConfig/usetheme';

const HomeClientSection = () => {
  const colors = useTheme();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const scrollContainerRef = useRef(null);
  const sectionRef = useRef(null);
  
  // State for drag scrolling
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [autoScrollEnabled, setAutoScrollEnabled] = useState(true);

  // Fetch clients from API
  useEffect(() => {
    const fetchClients = async () => {
      try {
        setLoading(true);
        const response = await fetch('https://api.dremerz.net/api/DZ_8923_Idlbay_Clients_ClientList');
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        console.log('API Response:', data);
        setClients(data);
      } catch (err) {
        console.error('Error fetching clients:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchClients();
  }, []);

  // Intersection Observer for triggering animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  // Auto-scroll animation effect
  useEffect(() => {
    if (!scrollContainerRef.current || !autoScrollEnabled || isDragging) return;
    
    const container = scrollContainerRef.current;
    const track = container.querySelector('.client-logos-track');
    const contentWidth = track.scrollWidth / 3; // Since we're tripling the content
    
    let animationId;
    let startTime = null;
    const duration = 40000; // 40 seconds for a full loop
    
    const animateScroll = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const elapsed = timestamp - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      container.scrollLeft = progress * contentWidth;
      
      if (progress < 1) {
        animationId = requestAnimationFrame(animateScroll);
      } else {
        // Reset and restart animation
        container.scrollLeft = 0;
        startTime = null;
        animationId = requestAnimationFrame(animateScroll);
      }
    };
    
    if (isVisible && autoScrollEnabled && !isDragging) {
      animationId = requestAnimationFrame(animateScroll);
    }
    
    return () => {
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
    };
  }, [isVisible, autoScrollEnabled, isDragging]);

  // Drag scrolling handlers
  const handleMouseDown = (e) => {
    setIsDragging(true);
    setAutoScrollEnabled(false);
    setStartX(e.pageX - scrollContainerRef.current.offsetLeft);
    setScrollLeft(scrollContainerRef.current.scrollLeft);
  };

  const handleMouseLeave = () => {
    if (isDragging) {
      setIsDragging(false);
      // Re-enable auto-scroll after a delay when user stops interacting
      setTimeout(() => setAutoScrollEnabled(true), 3000);
    }
  };

  const handleMouseUp = () => {
    if (isDragging) {
      setIsDragging(false);
      // Re-enable auto-scroll after a delay when user stops interacting
      setTimeout(() => setAutoScrollEnabled(true), 3000);
    }
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    const x = e.pageX - scrollContainerRef.current.offsetLeft;
    const walk = (x - startX) * 2; // Scroll speed multiplier
    scrollContainerRef.current.scrollLeft = scrollLeft - walk;
  };

  // Touch events for mobile
  const handleTouchStart = (e) => {
    setIsDragging(true);
    setAutoScrollEnabled(false);
    setStartX(e.touches[0].pageX - scrollContainerRef.current.offsetLeft);
    setScrollLeft(scrollContainerRef.current.scrollLeft);
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    const x = e.touches[0].pageX - scrollContainerRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    scrollContainerRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleTouchEnd = () => {
    if (isDragging) {
      setIsDragging(false);
      // Re-enable auto-scroll after a delay when user stops interacting
      setTimeout(() => setAutoScrollEnabled(true), 3000);
    }
  };

  if (loading) return <div className="text-center py-5">Loading client logos...</div>;
  if (error) return <div className="text-center py-5">Error: {error}</div>;
  if (clients.length === 0) return <div className="text-center py-5">No clients found</div>;

  // Create multiple copies for seamless loop
  const duplicateClients = [...clients, ...clients, ...clients];

  return (
    <div className="client-section" ref={sectionRef}>
      <div 
        className={`client-logos-container ${isVisible ? 'visible' : ''}`} 
        ref={scrollContainerRef}
        onMouseDown={handleMouseDown}
        onMouseLeave={handleMouseLeave}
        onMouseUp={handleMouseUp}
        onMouseMove={handleMouseMove}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        // style={{ cursor: isDragging ? '' : '' }}
      >
        <div className="client-logos-track">
          {duplicateClients.map((client, index) => (
            <div
              key={`${client.id}-${Math.floor(index / clients.length)}-${index}`}
              className="client-logo-item"
            >
              <img 
                src={`https://api.dremerz.net/uploads/${client.companyLogo}`} 
                alt={client.companyname} 
                className="client-logo"
                onError={(e) => {
                  console.error(`Failed to load logo: ${client.companyLogo}`);
                  e.target.style.display = 'none';
                  // Fallback to company name if image fails
                  const fallbackElement = document.createElement('div');
                  fallbackElement.className = 'client-logo-fallback';
                  fallbackElement.textContent = client.companyname;
                  e.target.parentNode.appendChild(fallbackElement);
                }}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default HomeClientSection;